import { AuthContext } from '@/auth/context';
import { AnonymousUserContext, buildClientFFUserContext } from '@airreading/feature-flags/web';
import * as Sentry from '@sentry/react';
import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import { PropsWithChildren, useContext, useEffect } from 'react';

/**
 * Returns the LaunchDarkly client ID from the environment variable.
 * If the environment variable is not set, it returns an empty string.
 *
 * @returns The LaunchDarkly client ID or an empty string
 */
function getLaunchDarklyClientId(): string {
  const LAUNCH_DARKLY_CLIENT_ID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID_KEY;
  if (!LAUNCH_DARKLY_CLIENT_ID) {
    Sentry.captureException(new Error('LAUNCH_DARKLY_CLIENT_ID is empty'));
    return '';
  }
  return LAUNCH_DARKLY_CLIENT_ID;
}

function LDProviderComponent({ children }: PropsWithChildren) {
  const { actingUser } = useContext(AuthContext);
  const ldClient = useLDClient();

  useEffect(() => {
    if (actingUser) {
      const userContext = buildClientFFUserContext(actingUser);
      ldClient?.identify(userContext);
    }
  }, [actingUser, ldClient]);

  return <>{children}</>;
}

export const LDProvider = withLDProvider({
  /**
   * If the clientSideID is empty or invalid, the rest of the app will launch as normal
   * but all feature flags will evaluate to default value.
   */
  clientSideID: getLaunchDarklyClientId(),
  /**
   * Use a shared anonymous context with a hardcoded key to prevent inflating MAU counts.
   * We don't have any use case to target anonymous users so we can just use a shared key.
   * Without this default, LD is initialized with a random anonymous key and therefore counts
   * each app-launch as a separate MAU.
   */
  context: AnonymousUserContext,
  options: {
    // Bootstrap with local storage to ensure flags are available immediately
    // It still fetches the flags from the server in the background
    bootstrap: 'localStorage',
  },
  reactOptions: {
    // Disable automatic camel casing casting to match the flag keys defined in
    // packages/common/src/feature-flags.ts (which are kebab-case).
    useCamelCaseFlagKeys: false,
  },
})(LDProviderComponent) as React.FC<PropsWithChildren>;
